<template>
    <div class="editor">
        <div class="content-wrapper">

            <div class="mb-3">
                <div v-if="error" class="alert alert-danger my-2" role="alert">
                    {{ error }}
                </div>
                <label for="assignment-name" class="section-heading">{{ lang['assignment.name'] }}</label>
                <input id="assignment-name" class="form-control" type="text" v-model="assignment.name" @keyup="resetError">
            </div>

			<div class="edit-row mt-3">
				<label for="is-open">{{ lang['assignment.open'] }}</label>
				<label class="switch">
					<input type="checkbox" v-model="assignment.is_open">
					<span class="slider-switch round"></span>
				</label>
			</div>

            <div class="edit-row mt-3" v-if="assignment.is_open">
                <label class="section-heading">{{ lang['assignment.code'] }}</label>
                <div id="assignment-code">{{assignment.code}}</div>
                <div>
                    <button class="btn btn-primary" @click="copyLink">Copy Link</button>
                </div>
            </div>

			<label for="options" class="section-heading d-flex justify-content-between align-items-center mt-3" @click="collapsed = !collapsed">{{ lang['general.options'] }} <i class="expand-options far" :class="{'fa-chevron-down': collapsed, 'fa-chevron-up': !collapsed}"></i></label>
			<div :class="{'options-hidden': collapsed, 'options-visible': !collapsed}">


				<div class="edit-row mt-3">
					<label for="feedback">{{ lang['assignment.feedback_level'] }}</label>
					<div>
						<div id="feedback" class="form-check form-check-inline">
							<input class="form-check-input" type="radio" name="feedbackOptions" id="feedback0" value="0" v-model="assignment.feedback">
							<label class="form-check-label" for="feedback1">0</label>
						</div>
						<div class="form-check form-check-inline">
							<input class="form-check-input" type="radio" name="feedbackOptions" id="feedback1" value="1" v-model="assignment.feedback">
							<label class="form-check-label" for="feedback1">1</label>
						</div>
						<div class="form-check form-check-inline">
							<input class="form-check-input" type="radio" name="feedbackOptions" id="feedback2" value="2" v-model="assignment.feedback">
							<label class="form-check-label" for="feedback2">2</label>
						</div>
						<div class="form-check form-check-inline">
							<input class="form-check-input" type="radio" name="feedbackOptions" id="feedback3" value="3" v-model="assignment.feedback">
							<label class="form-check-label" for="feedback3">3</label>
						</div>
					</div>
				</div>
				<div class="edit-row">
					<div v-if="assignment.feedback == 0" class="prompt">
						<div class="feedback">{{ lang['assignment.no_feedback'] }}</div>
						<div>{{ lang['assignment.no_feedback_description'] }}</div>
					</div>
					<div v-if="assignment.feedback == 1" class="prompt">
						<div class="feedback">{{ lang['assignment.limited_feedback'] }}</div>
						<div>{{ lang['assignment.limited_feedback_description'] }}</div>
					</div>
					<div v-if="assignment.feedback == 2" class="prompt">
						<div class="feedback">{{ lang['assignment.specific_feedback'] }}</div>
						<div>{{ lang['assignment.specific_feedback_description'] }}</div>
					</div>
					<div v-if="assignment.feedback == 3" class="prompt">
						<div class="feedback">{{ lang['assignment.immediate_feedback'] }}</div>
						<div>{{ lang['assignment.immediate_feedback_description'] }}</div>
					</div>
				</div>

				<div class="edit-row mt-2" >
					<label for="flashcard">{{ lang['assignment.flashcard_pool'] }}</label>
					<label class="switch">
						<input type="checkbox" v-model="assignment.no_flashcard">
						<span class="slider-switch round"></span>
					</label>
				</div>

				<div class="edit-row">
					<div class="prompt">
						{{ lang['assignment.flashcard_pool_description'] }}
					</div>
				</div>
			</div>

            <div v-if="!this.showProblems" id="add_problem_header" class="d-flex justify-content-between align-items-center mt-2 mb-1">
                <label for="problems" class="section-heading">{{ lang['general.problems'] }}</label>
                <div class="new-problem" @click="expandList">{{ lang['assignment.add_problems'] }} <i class="fas fa-plus-circle ml-1"></i></div>
            </div>

            <div v-if="this.showProblems" id="close_problem_header" class="d-flex justify-content-between align-items-center mt-2 mb-1">
                <label for="problems" class="section-heading">{{ lang['assignment.add_problems'] }}</label>
                <div class="close-list" @click="expandList"><i class="fal fa-times ml-1"></i></div>
            </div>

			<div style="right: inherit; position: inherit; top: inherit;"  class="drawer-dropdown-list mb-1" :class="{'list-open': showProblems, 'list-closed': !showProblems}">
				<div v-for="(problem, index) in listProblems" class="dropdown-choice d-flex justify-content-between" :class="setActiveProblem(index)" @mouseenter="emitPathHighlight('on', problem)" @mouseleave="emitPathHighlight('off')">
					<div class="clickable" @click="editProblem(problem, index, true)" v-html=problem.name>PROBLEM</div>
                    <div class="d-flex">
                        <div class="mr-2" v-if="problem.used > 0" style="color:#8E8D8D">{{ problem.used }}</div>
    				    <div class="my-auto" @click="addProblem(problem)"><i class="new-problem fas fa-plus-circle ml-1"></i></div>
                    </div>
				</div>
			</div>

            <div v-if="loadingProblems" class="mt-4 flex-r flex-center" style="font-size: 1.25em;">
                <div><i class="far fa-spinner fa-spin mr-2 blue"></i> {{ lang['general.loading'] }}</div>
            </div>
            <div v-else>
                <draggable v-model="problems" @start="drag=true" @change="reorder" @end="drag=false">
                    <div v-for="(problem, index) in problems" class="problem-card" :class="setActive(index)" @mouseenter="emitPathHighlight('on', problem)" @mouseleave="emitPathHighlight('off')" >
                        <div class="flex-r">
                            <div @click="editProblem(problem, index)" class="prefix mr-1">{{ index + 1 }}. </div>
                            <div v-if="!problem.is_valid" @click="editProblem(problem, index)" class="prefix mr-1 my-auto"><i title="This problem must be linked to a valid path. Until then, students will not see it when doing the assignment." class="far fa-exclamation-triangle red"></i></div>
                            <div @click="editProblem(problem, index)" class="problem-text text-nowrap" v-text=problem.name>PROBLEM</div>
                        </div>
                        <div class="ml-2 my-auto flex-end flex-stiff" title="Remove problem from assignment">
                            <i class="fal fa-times red" @click="removeProblem(problem)"></i>
                        </div>
                    </div>
                </draggable>
            </div>
        </div>
    </div>

</template>

<script>
import draggable from 'vuedraggable'
export default {
    components: {
        draggable,
    },
    props: ['content', 'model'],
        data() {
            return {
                assignment: {
                    feedback: 2,
                    problem_ids: [],
                },
                collapsed: false,
                isNew: false,
                error: false,
                loadingProblems: false,
                problems: [], //the assignments problems
                assignmentProblems:[], //all model problems
                listProblems: [], //problems to add to assigment
                showProblems: false,
                lang: lang,
                selectedIndex: -1,
                selectedProblemIndex: -1,
            }
        },

	mounted() {
            if(this.content.assignment) {
                this.loadingProblems = true;
                this.assignment = this.content.assignment;
                this.getAssignmentProblems();
                this.collapsed = true;
            }
            this.getModelProblems();
            this.assignment.model_id = this.model;
	},

    methods: {

        addProblem(problem) {
            if(this.assignment.problem_ids.indexOf(problem.id) ===  -1) {
                this.problems.push(problem);
                this.assignment.problem_ids = this.problems.map(a => a.id);
                this.listProblems.splice(this.listProblems.indexOf(problem), 1);
            }
        },

        removeProblem(problem) {
            this.problems.splice(this.problems.indexOf(problem), 1);
            this.assignment.problem_ids = this.problems.map(a => a.id);
            this.listProblems.push(problem);
            bus.$emit('removeHighlight');
        },

        copyLink() {
            const el = document.createElement('textarea');
            el.value = "https://www.decisionbasedlearning.com/code/" + this.assignment.code;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
        },

        closeEditor() {
            this.$emit('close', this.isNew, this.item);
        },

        reorder() {
            this.assignment.problem_ids = this.problems.map(a => a.id);
        },

        editProblem(problem, index, addProblem) {
            if (addProblem) {
                this.selectedIndex = -1;
                this.selectedProblemIndex = index;
            } else {
                this.selectedIndex = index;
                this.selectedProblemIndex = -1;
            }
            editingProblem = true;
            bus.$emit('editProblem', problem, null, true);
        },

        emitPathHighlight(state, problem) {
            if(state == 'on') {
                bus.$emit('highlightPath', problem);
            } else {
                bus.$emit('removeHighlight');
            }
        },

        expandList() {
            bus.$emit('checkTimeout');
            this.listProblems = [];
            this.assignmentProblems.forEach(assignmentProblem => {
                if(this.problems.filter(problem => problem.id == assignmentProblem.id).length == 0) {
                    this.listProblems.push(assignmentProblem);
                };
            });
            this.showProblems = !this.showProblems;
        },

        getModelProblems() {//all model problems
            var $this = this;
            $.get('/models/' + this.model +'/problems', function(response) {
                response.problems.forEach(problem => {
                    if(problem.path_id > 0) {
                        $this.assignmentProblems.push(problem);
                    }
                })
            })
            .catch((error) => {
                bus.$emit('popupError', error);
            });
        },

        getAssignmentProblems() {// assignment problems...
            var $this = this;
            $.get('/problemsets/' + this.assignment.id + '/model-id/' + this.model, function(response) {
                $this.loadingProblems = false;
                response.forEach(problem => {
                    if (problem) {
                        $this.problems.push(problem);
                    }
                });
            })
            .catch((error) => {
                bus.$emit('popupError', error);
            });
        },

        resetError() {
            this.error = false;
        },

        save() {
            if(!this.assignment.name || this.assignment.name.trim().length == 0) {
                this.error = lang['assignment.name_prompt'];
            } else if (this.assignment.name.length > 140) {
                this.error = lang['assignment.name_length_prompt'];
            } else {
                var $this = this;
                this.assignment.no_flashcard = this.assignment.no_flashcard ? 1 : 0;
                this.assignment.is_open = this.assignment.is_open ? 1 : 0;
                $.post('/problemsets/save', this.assignment,
                    (res) => {
                        if(!res.error) {
                            $this.closeEditor();
                      };
                    })
                .catch((error) => {
                    bus.$emit('popupError', error);
                });
            }
        },

        setActive(index) {
            return this.selectedIndex == index ? 'selected' : '';
        },

        setActiveProblem(index) {
            return this.selectedProblemIndex == index ? 'selected' : '';
        },
    },

    created() {
            bus.$on('save', this.save);
            bus.$on('update', this.getAssignmentProblems);
    },

    beforeDestroy() {
            bus.$off('save', this.save);
            bus.$off('update', this.getAssignmentProblems);
    }

}

</script>
